import { ChatMessageType, IChatMessage } from "@components/chatMessage/ChatMessage.utils";
import { IGetTileDataArgs } from "@components/dashboard";
import { EvalaPlainIcon } from "@components/icon";
import Field from "@components/inputs/field";
import { ISelectionChangeArgs } from "@components/inputs/select/Select.types";
import UserIconSelect from "@components/inputs/select/UserIconSelect";
import { IFieldDefFn, IGetValueArgs } from "@components/smart/FieldInfo";
import BindingContext, { createPath, IEntity } from "@odata/BindingContext";
import { UserPermissionCodes } from "@odata/CompanyPermissionEnums";
import {
    ChatEntity, ChatParticipantRecipientEntity,
    ChatParticipantSenderEntity,
    EntityTypeName,
    IChatEntity,
    IChatMessageEntity,
    IFileMetadataEntity,
    IUserEntity,
    IUserNotificationEntity,
    UserEntity
} from "@odata/GeneratedEntityTypes";
import { NotificationTypeCode } from "@odata/GeneratedEnums";
import { OData } from "@odata/OData";
import { parseResponse } from "@odata/ODataParser";
import { getCompanyLogoUrl } from "@utils/CompanyUtils";
import customFetch, { getDefaultPostParams } from "@utils/customFetch";
import { logger } from "@utils/log";
import { INotificationWebsocketMessage, TWebsocketMessage } from "@utils/websocketManager/Websocket.types";
import { TRecordAny } from "global.types";
import i18next from "i18next";
import React from "react";

import Avatar from "../../components/avatar/Avatar";
import {
    GET_GLOBAT_CHAT_COUNT,
    GET_GLOBAT_CHAT_CUSTOMER_COUNT,
    GET_GLOBAT_CHAT_CUSTOMER_COUNT_CROSS_COMPANY, HANDOVER_CHATS
} from "../../constants";
import { ChatOperationType, IAppContext } from "../../contexts/appContext/AppContext.types";
import { AvatarSize, IconSize, Status } from "../../enums";
import { Model } from "../../model/Model";
import {
    fetchNotificationsMemoized,
    getNotificationArguments
} from "../../notifications/Notification.utils";
import { getLocalTimeDate, getUtcDate, getUtcDayjs } from "../../types/Date";
import { FormStorage } from "../../views/formView/FormStorage";
import {
    CustomerGeneralRoleId,
    isUserCustomer
} from "../admin/users/Users.utils";

export interface IChatOperationArgs {
    type: ChatOperationType;
    documentBc?: BindingContext;
    chatBc?: BindingContext;
    id?: string;
    data: ISendToAccountantChatData;
}

export interface IChatData {
    user: IEntity;
    isOpen: boolean;
    draftId?: number;
}

export interface ISendToAccountantChatData {
    user?: IUserEntity;
    messages?: IChatMessageEntity[];
    title?: string;
}


export const CUSTOMER_GROUP = "Customer";
export const USER_GROUP = "User";

export const getUserSelectGroups = () => {
    return [{
        id: CUSTOMER_GROUP,
        title: i18next.t("Users:Customer")
    }, {
        id: USER_GROUP,
        title: i18next.t("Users:User")
    }];
};

export const isSaved = (args: IGetValueArgs): boolean => !args.storage.data.bindingContext.isNew();

export function hasUnreadMessage(storage: Model, entity: IChatEntity): boolean {
    const user = storage.context.getData().userSettings;
    const myId = user?.Id;

    const myPropName = getMyPropName(storage.context, entity);
    if (myPropName) {
        const dateLastRead = entity[myPropName]?.DateLastRead;
        const foreignMessages = entity.Messages?.filter(msg => msg.CreatedBy?.Id !== myId);
        const count = foreignMessages?.length;
        const lastDate = foreignMessages[count - 1]?.DateLastModified;

        if (!dateLastRead && foreignMessages?.length > 0) {
            return true;
        }

        const dateA = getUtcDayjs(lastDate);
        const dateB = getUtcDayjs(dateLastRead);

        return dateA > dateB;
    }

    return false;
}

export const getMyPropName = (context: IAppContext, entity: IChatEntity) => {
    const user = context.getData().userSettings;
    const myId = user?.Id;

    const senderId = entity[ChatEntity.Sender]?.[ChatParticipantSenderEntity.User]?.Id;
    const recipientId = entity[ChatEntity.Recipient]?.[ChatParticipantRecipientEntity.User]?.Id;

    const isMine = senderId === myId || recipientId === myId;
    if (!isMine) {
        return null;
    }

    return senderId === myId ? ChatEntity.Sender : ChatEntity.Recipient;
};

export function getMessageIcon(context: IAppContext, isAgendaMessage: boolean): React.ReactNode {
    if (isAgendaMessage) {
        const logoURL = getCompanyLogoUrl(context.getCompany());
        return (
            <Avatar src={logoURL} size={AvatarSize.almostM} transparent/>
        );
    }
    return (<EvalaPlainIcon width={IconSize.L} height={IconSize.L}/>);
}

function parseChatMessage(storage: FormStorage<IChatEntity>, m: IChatMessageEntity, type: ChatMessageType, isFirst?: boolean): IChatMessage {
    const _isAgendaMessage = isUserCustomer(m.CreatedBy);

    const createdBy = m.CreatedBy?.Id;
    const isMine = createdBy === storage.context.getData().userSettings?.Id;
    const message: IChatMessage = {
        icon: getMessageIcon(storage.context, _isAgendaMessage),
        created: getUtcDate(m.DateCreated),
        createdBy: m.CreatedBy?.Name,
        createdById: m.CreatedBy?.Id,
        text: m.Content,
        type: type ?? (isMine ? ChatMessageType.Sent : ChatMessageType.Received),
        attachments: m.Attachments?.map(attachment => attachment.File)
    };
    if (isFirst) {
        message.subject = storage.data.entity?.Title ?? "";
    }
    return message;
}

// When user creates new message, it doesn't contain some information as icon, created info - this function adds it
export function getChatMessageFromCreatedMessage(storage: FormStorage<IChatEntity>, m: IChatMessage, files: IFileMetadataEntity[]): IChatMessageEntity {
    const { context, data: { entity } } = storage;
    const user = context.getData().userSettings;
    const message: IChatMessageEntity = {
        Content: m.text,
        DateCreated: getLocalTimeDate(),
        CreatedBy: user
    };

    if (files?.length) {
        message.Attachments = files.map(f => ({
            File: f
        }));
    }
    return message;
}

export function getStoredMessages(storage: FormStorage<IChatEntity>, isChatMine: boolean): IChatMessage[] {
    const { entity } = storage.data;

    let createBy = entity.Messages?.[0]?.CreatedBy?.Id;
    let type = ChatMessageType.Sent;

    return entity.Messages?.map((m, idx) => {
        if (m?.CreatedBy?.Id !== createBy) {
            type = type === ChatMessageType.Sent ? ChatMessageType.Received : ChatMessageType.Sent;
        }

        createBy = m?.CreatedBy?.Id;

        return parseChatMessage(storage, m, isChatMine ? null : type, idx === 0);
    }) ?? [];
}

export async function checkChatMessageNotification(message: TWebsocketMessage, oData: OData): Promise<TRecordAny> {
    const notificationId = (message as INotificationWebsocketMessage).Parameters.Id;
    const result = await fetchNotificationsMemoized(oData, notificationId);
    const { Notification } = (result.value as IUserNotificationEntity) ?? {};
    const args = getNotificationArguments(Notification);
    const isChatMessage = Notification.NotificationTypeCode === NotificationTypeCode.NewChatMessage;
    const isChat = Notification.NotificationTypeCode === NotificationTypeCode.NewChat;

    return {
        isChat,
        isChatMessage,
        ...args
    };
}

export const getLinkPropertyByEntityType = (entityType: EntityTypeName, isDraft: boolean) => {
    const _appendDraft = (name: string) => {
        return `${name}${isDraft ? 'Draft' : ''}`;
    };
    switch (entityType) {
        case EntityTypeName.BankTransaction:
        case EntityTypeName.CashReceiptIssued:
        case EntityTypeName.CashReceiptReceived:
        case EntityTypeName.LoanTransaction:
            return _appendDraft(ChatEntity.PaymentDocument);

        case EntityTypeName.InboxFile:
            return ChatEntity.InboxFile;
    }

    return _appendDraft(ChatEntity.Document);
};

export const isMessageMine = (user: IUserEntity, entity: IEntity) => {
    return entity?.[ChatEntity.Sender]?.User?.Id === user?.Id || entity?.[ChatEntity.Recipient]?.User?.Id === user?.Id;
};

export const hasCustomer = (entity: IChatEntity) => {
    const senderId = entity?.[ChatEntity.Sender]?.[ChatParticipantSenderEntity.User]?.[UserEntity.GeneralRole]?.Id;
    const recipientId = entity?.[ChatEntity.Recipient]?.[ChatParticipantRecipientEntity.User]?.[UserEntity.GeneralRole]?.Id;
    return senderId === CustomerGeneralRoleId || recipientId === CustomerGeneralRoleId;
};

export const hasTakeOverPermission = (context: IAppContext) => {
    return context.getCompanyPermissions().has(UserPermissionCodes.ChatTakeover);
};

export const canTakeOverChat = (entity: IChatEntity, context: IAppContext) => {
    const currentUser = context.getData().userSettings;
    const isCurrentUserCustomer = isUserCustomer(currentUser);
    const isMyConversation = isMessageMine(currentUser, entity);
    return !isCurrentUserCustomer && hasCustomer(entity) && (isMyConversation || hasTakeOverPermission(context));
};

export const myUserProp = (context: IAppContext, entity: IChatEntity) => {
    const myId = context.getData().userSettings.Id;
    if (entity?.[ChatEntity.Sender]?.User?.Id === myId) {
        return ChatEntity.Sender;
    }

    if (entity?.[ChatEntity.Recipient]?.User?.Id === myId) {
        return ChatEntity.Recipient;
    }

    return null;
};

export const participantFilterRenderer = (args: IFieldDefFn) => {
    const _change = (e: ISelectionChangeArgs) => {
        args.props.onChange?.({
            ...e,
            bindingContext: args.props.info?.bindingContext
        });
    };

    return <Field label={args.storage.t("Chat:Attendant")}>
        <UserIconSelect
            allowEmpty
            isCustomer={false}
            value={args.props.value}
            onChange={_change}
        />
    </Field>;
};


export const getParticipantQuery = (userId: number) => {
    const sender = createPath(ChatEntity.Sender, ChatParticipantSenderEntity.User, UserEntity.Id);
    const recipient = createPath(ChatEntity.Recipient, ChatParticipantSenderEntity.User, UserEntity.Id);

    return `(${sender} eq ${userId} OR ${recipient} eq ${userId})`;

};

export const getUnreadMessagesWithCustomersCountCrossCompany = async (args?: IGetTileDataArgs) => {
    try {
        const response = await customFetch(GET_GLOBAT_CHAT_CUSTOMER_COUNT_CROSS_COMPANY);
        if (response?.ok) {
            const data = await response.json();
            return data;
        }
    } catch (e) {
        logger.error(`Error loading unread message count`, e);
    }

    return null;
};


export const getUnreadMessagesWithCustomersCount = async (args?: IGetTileDataArgs) => {
    try {
        const response = await customFetch(GET_GLOBAT_CHAT_CUSTOMER_COUNT);
        if (response?.ok) {
            const data = await response.json();
            return data?.UnreadChats?.length ?? 0;
        }
    } catch (e) {
        logger.error(`Error loading unread message count`, e);
    }

    return 0;
};

export const getUnreadMessagesCount = async (args?: IGetTileDataArgs) => {
    try {
        const response = await customFetch(GET_GLOBAT_CHAT_COUNT);
        if (response?.ok) {
            const data = await response.json();
            return data?.UnreadChats?.length ?? 0;
        }
    } catch (e) {
        logger.error(`Error loading unread message count`, e);
    }

    return 0;
};

export const handover = async (chatIds: string[], userId: string) => {
    const body = {
        ChatIds: chatIds?.map(id => parseInt(id, 10)),
        User: { "@odata.id": `Users(${userId})` }
    };

    const _setError = (detailData: TRecordAny) => {
        return {
            status: Status.Error,
            title: i18next.t("Common:Errors.ErrorHappened"),
            detailData: detailData,
            shouldAddBottomMargin: true
        };
    };

    try {
        const response = await customFetch(HANDOVER_CHATS, {
            ...getDefaultPostParams(),
            body: JSON.stringify(body)
        });
        if (!response.ok) {
            const parsed: TRecordAny = await parseResponse(response);
            return _setError(parsed);
        }
    } catch (error) {
        logger.error("Error handover to another user:", error);
        return _setError(error);
    }

    return null;
};